import { useState, useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import { uiActions } from "../store/reducers/ui";
import { loadingActions } from "../store/reducers/loading";

import axios from "axios";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  responseType: "json",
});

const debug = process.env.REACT_APP_DEBUG === "true";

/**Hook to automate some of the process of sending a HTTP request, and processing the response and results
 * @param options initial loading: true if the page depends on the results to load - The other options are messages to be displayed accordingly to results.
 * @return isLoading: Hook loading state
 * @return sendRequest: Function to send and process the request
 * @return error: If any errors
 * @author Diego Weidle Rost
 */
export default function useApi(options) {
  const { initialLoading, successMessage, errorMessage, liveErrors } = options;

  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(initialLoading ? initialLoading : false);
  const [error, setError] = useState(null);

  /**Displays snackbar message. and log to the console (if debug enabled)
   * @param type message type (success, warning, or error)
   * @param message the message to be displayed
   */
  const dispatchMessage = useCallback(
    (type, message) => {
      if (debug || liveErrors) {
        if (type === "error") {
          console.error(message);
        } else {
          console.log(message);
        }
      }
      dispatch(uiActions.infoMessage({ open: true, type, message }));
    },
    [dispatch, liveErrors]
  );

  /**Checks the response and displays resulting messages accordingly
   * @param response the response received from the backend
   * @param method request method (GET or POST)
   * @return data if available or null
   */
  const handleResponse = useCallback(
    (response, method) => {
      if (response) {
        if (response.status === 200) {
          const data = response.data || null;

          if (data.result === "success") {
            if (method === "GET") {
              if (debug && successMessage) {
                console.log(successMessage);
              }
            } else if (successMessage) {
              dispatchMessage("success", successMessage);
            }
          } else {
            throw new Error(data.error);
          }

          return data;
        } else {
          throw new Error(response.status + " response");
        }
      } else {
        throw new Error("No response from endpoint");
      }
    },
    [dispatchMessage, successMessage]
  );

  /**Sends the HTTP request to the backend and process the response if a callback function is provided.
   * Also displays resulting messages to the user, and logs the messages (if debug enabled)
   * @param requestConfig contains the endpoint url and payload to send
   * @param callback the callback function to process the response data
   * @return data if available or null
   */
  const sendRequest = useCallback(
    async (requestConfig, callback) => {
      const { url, payload } = requestConfig;
      const method = requestConfig.method ? requestConfig.method : "GET";
      let response = null;

      if (debug) {
        console.log("Sending " + method + " request...");
      }

      setIsLoading(true);
      dispatch(loadingActions.increment());

      try {
        if (method === "GET") {
          response = await api.get(url, { params: payload });
        } else {
          response = await api.post(url, payload);
        }

        const data = handleResponse(response, method);

        if (callback && data && data.result === "success") {
          callback(data);
        }

        return data;
      } catch (error) {
        const catchErrorMessage = error.message || error;

        setError(errorMessage ? errorMessage + ": " + catchErrorMessage : catchErrorMessage);

        return catchErrorMessage;
      } finally {
        setIsLoading(false);
        dispatch(loadingActions.decrement());
      }
    },
    [dispatch, errorMessage, handleResponse]
  );

  useEffect(() => {
    if (error) {
      dispatchMessage("error", error);
    }

    return () => {
      setError(null);
    };
  }, [dispatchMessage, error]);

  return {
    isLoading,
    sendRequest,
    error,
  };
}
